import React from "react";

import PropTypes from "prop-types";
import styled from "styled-components";

import {
  AdminButton,
} from "../action_engine";


/**
 * AdminFormCollection
 *
 * Generic presentation/orchestration primitive for an editable
 * collection inside Q360 Admin forms.
 *
 * Responsibilities:
 * - render an ordered collection;
 * - expose immutable add/remove/replace callbacks;
 * - provide accessible generic collection actions;
 * - delegate item rendering to the consumer.
 *
 * Explicitly excluded:
 * - business-domain knowledge;
 * - persistence;
 * - business validation;
 * - automatic identifier generation;
 * - StudioDocument mutation;
 * - Runtime mutation.
 */
export default function AdminFormCollection({
  items,
  renderItem,
  onChange,
  createItem = null,
  addLabel = "Ajouter",
  removeLabel = "Supprimer",
  emptyLabel = "Aucun élément.",
  allowAdd = true,
  allowRemove = true,
}) {
  const collection =
    Array.isArray(items)
      ? items
      : [];


  const emit = (
    nextItems
  ) => {
    if (
      typeof onChange
        === "function"
    ) {
      onChange(
        nextItems
      );
    }
  };


  const addItem = () => {
    if (
      !allowAdd
      || typeof createItem
        !== "function"
    ) {
      return;
    }

    const nextItem =
      createItem();

    emit([
      ...collection,
      nextItem,
    ]);
  };


  const removeItem = (
    index
  ) => {
    if (!allowRemove) {
      return;
    }

    emit(
      collection.filter(
        (_, itemIndex) =>
          itemIndex !== index
      )
    );
  };


  const replaceItem = (
    index,
    nextItem
  ) => {
    emit(
      collection.map(
        (item, itemIndex) =>
          itemIndex === index
            ? nextItem
            : item
      )
    );
  };


  return (
    <CollectionRoot
      data-admin-form-collection
    >
      {collection.length === 0 ? (
        <EmptyState>
          {emptyLabel}
        </EmptyState>
      ) : (
        <CollectionList>
          {collection.map(
            (
              item,
              index
            ) => (
              <CollectionItem
                key={resolveItemKey(
                  item,
                  index
                )}
                data-admin-form-collection-item
              >
                <ItemContent>
                  {renderItem(
                    item,
                    index,
                    {
                      replaceItem:
                        (
                          nextItem
                        ) =>
                          replaceItem(
                            index,
                            nextItem
                          ),

                      removeItem:
                        () =>
                          removeItem(
                            index
                          ),
                    }
                  )}
                </ItemContent>

                {allowRemove ? (
                  <ItemActions>
                    <AdminButton
                      type="button"
                      variant="danger"
                      onClick={
                        () =>
                          removeItem(
                            index
                          )
                      }
                    >
                      {removeLabel}
                    </AdminButton>
                  </ItemActions>
                ) : null}
              </CollectionItem>
            )
          )}
        </CollectionList>
      )}

      {allowAdd
        && typeof createItem
          === "function"
        ? (
          <CollectionActions>
            <AdminButton
              type="button"
              onClick={addItem}
            >
              {addLabel}
            </AdminButton>
          </CollectionActions>
        )
        : null}
    </CollectionRoot>
  );
}


function resolveItemKey(
  item,
  index
) {
  if (
    item
    && typeof item
      === "object"
  ) {
    const candidate =
      item.id
      ?? item.uuid
      ?? item.key;

    if (
      typeof candidate
        === "string"
      && candidate.trim()
        !== ""
    ) {
      return candidate;
    }
  }

  return `admin-form-collection-${index}`;
}


AdminFormCollection.propTypes = {
  items:
    PropTypes.array,

  renderItem:
    PropTypes.func.isRequired,

  onChange:
    PropTypes.func.isRequired,

  createItem:
    PropTypes.func,

  addLabel:
    PropTypes.node,

  removeLabel:
    PropTypes.node,

  emptyLabel:
    PropTypes.node,

  allowAdd:
    PropTypes.bool,

  allowRemove:
    PropTypes.bool,
};


const CollectionRoot =
  styled.div`
    display: grid;
    gap: 1rem;
    min-width: 0;
  `;


const CollectionList =
  styled.div`
    display: grid;
    gap: 0.8rem;
  `;


const CollectionItem =
  styled.div`
    display: grid;
    gap: 0.8rem;

    padding: 0.9rem;

    border:
      1px solid
      rgba(
        255,
        255,
        255,
        0.1
      );

    border-radius: 12px;

    background:
      rgba(
        7,
        15,
        35,
        0.3
      );
  `;


const ItemContent =
  styled.div`
    min-width: 0;
  `;


const ItemActions =
  styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
  `;


const CollectionActions =
  styled.div`
    display: flex;
    justify-content: flex-start;
  `;


const EmptyState =
  styled.div`
    padding: 0.8rem;

    border:
      1px dashed
      rgba(
        255,
        255,
        255,
        0.12
      );

    border-radius: 10px;

    color:
      rgba(
        247,
        247,
        247,
        0.62
      );
  `;
